import { create } from 'zustand';
import useOrganizationStore from './orgState';
import useUserStore from './userState';
import getPlanDetails from '@/apis/getPlanDetails';
import cancelSubscription from '@/apis/cancelSubscription';
import headerObject from '@/types/headerObj';
import emptyHeaderObj from '@/types/emptyHeaderObj';

function normalizePlanDetailsPayload(
  raw: unknown
): Record<string, unknown> | null {
  if (raw === null || raw === undefined) {
    return null;
  }
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object') {
    return raw as Record<string, unknown>;
  }
  return null;
}

interface BillingPlanDetailsState {
  status: string;
  interval: string;
  projectedTotal: number;
  nextBillingDate: number;
  gitSynced: boolean;
  billingInterval: string; // "month" or "year"
  setBillingInterval: (interval: string) => void;
  /** Autumn / tier plan id when available (`free`, `startup`, …) */
  planTier: string;
  planName: string;
  tokensIncluded: number;
  tokensUsed: number;
  savedCardBrand: string;
  savedCardLast4: string;
  planDetailsLoaded: boolean;
  getPlanDetails: () => Promise<void>;
  cancelSubscription: () => Promise<void>;
}

const useBillingStore = create<BillingPlanDetailsState>()((set, get) => ({
  status: '',
  interval: '',
  projectedTotal: 0,
  nextBillingDate: 0,
  gitSynced: false,
  billingInterval: 'month',
  setBillingInterval: (interval: string) => set({ billingInterval: interval }),
  planTier: '',
  planName: '',
  tokensIncluded: 0,
  tokensUsed: 0,
  savedCardBrand: '',
  savedCardLast4: '',
  planDetailsLoaded: false,
  getPlanDetails: async () => {
    const org = useOrganizationStore.getState().organization;
    const user = useUserStore.getState().user;

    set({ planDetailsLoaded: false });
    try {
      if (!org || !user) {
        return;
      }

      if (!org.OrgUUID) {
        return;
      }

      const billPlanDetails = await getPlanDetails(org);
      const payload = normalizePlanDetailsPayload(billPlanDetails);
      if (!payload) {
        return;
      }

      const errRaw = payload.Error;
      const err = errRaw === undefined || errRaw === null ? '' : String(errRaw);
      if (err !== '') {
        return;
      }

      set({
        status: String(payload.Status ?? ''),
        interval: String(payload.Interval ?? ''),
        projectedTotal: Number(payload.ProjectedTotal ?? 0),
        nextBillingDate: Number(payload.NextBillingDate ?? 0),
        gitSynced: Boolean(payload.GitSynced),
        planTier: String(payload.PlanTier ?? ''),
        planName: String(payload.PlanName ?? ''),
        tokensIncluded: Number(payload.TokensIncluded ?? 0),
        tokensUsed: Number(payload.TokensUsed ?? 0),
        savedCardBrand: String(payload.SavedCardBrand ?? ''),
        savedCardLast4: String(payload.SavedCardLast4 ?? ''),
      });
    } finally {
      set({ planDetailsLoaded: true });
    }
  },
  cancelSubscription: async () => {
    const org = useOrganizationStore.getState().organization;
    const user = useUserStore.getState().user;
    if (!org || !user) return;

    const headerObj: headerObject = {
      ...emptyHeaderObj,
      Useruuid: user.UserUUID,
      Useremail: user.Email,
      Username: user.FirstName,
      Orgname: org.OrgName,
      Orguuid: org.OrgUUID,
    };

    const planStatus = await cancelSubscription(org, headerObj);
    if (!planStatus?.Error || planStatus?.Error === '') {
      set({
        status: 'cancelled',
        interval: '',
        nextBillingDate: 0,
        planTier: '',
        planName: '',
        tokensIncluded: 0,
        tokensUsed: 0,
      });
    }
  },
}));

export default useBillingStore;
